import { FC, useCallback, useMemo, useRef, useState } from "react";
import { endOfWeek, format, isAfter, isWithinInterval, startOfWeek } from "date-fns";
import { generatePath, Link, useSearchParams } from "react-router-dom";
import { Routes } from "router";
import { ArrowRight, Edit } from "theme/icons";

import { RawOptionsFromRefiners } from "@fullcalendar/core/internal";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import { Box, CircularProgress, Grid2, useTheme } from "@mui/material";
import Popover from '@mui/material/Popover';
import { styled } from "@mui/system";

import { useBookings } from "../../../api/hooks/useBookings/useBookings.ts";
import useHalls from "../../../api/hooks/useHalls/useHalls.ts";
import { useUpdateBooking } from "../../../api/hooks/useUpdateBooking/useUpdateBooking.ts";
import Typography from "../../../theme/components/Typography/Typography.tsx";
import { convertStingToDate } from "../../../utils/dateHelpers.ts";

class ResourceRefiners {
}

type WeekCalendarProps = {
  hallId: string;
}

export const StyledPopover = styled(Popover)(({ theme}) => {
  return {
    "& .MuiPaper-root.MuiPopover-paper": {
      backgroundColor: 'white',
      padding: theme.spacing(2),
      minWidth: 200,
    }
  }
})

const WeekCalendar:FC<WeekCalendarProps> = ({ hallId }) => {
  const calendarRef = useRef<FullCalendar | null>(null);
  const { mutateAsync } = useUpdateBooking('');
  const { palette } = useTheme();

  const [searchParams, setSearchParams] = useSearchParams();

  const { halls, isFetching } = useHalls();

  const resources = useMemo<RawOptionsFromRefiners<Required<ResourceRefiners>>>(() => {
    const selectedHall = halls?.find(h => String(h.id) === String(hallId));

    return selectedHall ? [{
      id: selectedHall.id,
      title: selectedHall.name,
    }] : [];
  }, [halls, hallId]);

  const startDate = useMemo(() => convertStingToDate(searchParams.get("d")) ? startOfWeek(convertStingToDate(searchParams.get("d"))!, { weekStartsOn: 1 }) : startOfWeek(new Date(), { weekStartsOn: 1 }), [searchParams]);
  const endDate = useMemo(() => convertStingToDate(searchParams.get("d")) ? endOfWeek(convertStingToDate(searchParams.get("d"))!, { weekStartsOn: 1 }) : endOfWeek(new Date(), { weekStartsOn: 1 }), [searchParams])

  const { data, isPending: isBookingsFetching, refetch } = useBookings({
    startDate: format(startDate, "yyyy-MM-dd"),
    endDate: format(endDate, "yyyy-MM-dd"),
    hallId,
    page: 1,
    pageSize: 100,
    sendDeleted: false
  });

  const eventDropped = useCallback(async (eventDropInfo: any) => {
    const result = confirm('Підвердити зміну дати?');

    if (!result){
      eventDropInfo.revert();
    }else{
      try {
        await mutateAsync({
          id: eventDropInfo.event.id,
          hallId: eventDropInfo.newResource?.id || null,
          startDate: format(new Date(eventDropInfo.event.startStr), "yyyy-MM-dd'T'HH:mm:ss.SSS"),
          endDate: format(new Date(eventDropInfo.event.endStr), "yyyy-MM-dd'T'HH:mm:ss.SSS"),
        })
        await refetch();
      }catch (e){
        console.error(e);
        eventDropInfo.revert();
      }
    }
  }, [mutateAsync, refetch]);

  const getEventBackgroundColor = useCallback((status: string) => {
    switch (status) {
      case "PaymentPending":
        return palette.warning.main;
      case "PaymentCompleted":
        return palette.success.main;
      case "PaymentMade":
        return '#FFA500';
      case "PaymentCanceled":
        return palette.error.main;
      default:
        return palette.error.main;
    }
  }, [palette.warning.main, palette.success.main, palette.error.main])

  const tableEvents = useMemo(() => data?.bookings?.map(b => ({
    id: b.bookingId,
    title: `${b.clientName}`,
    durationEditable: false,
    editable: isAfter(new Date(b.startDate), new Date(Date.now() - 4 * 60 * 60 * 1000)),
    resourceId: hallId,
    start: format(b.startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
    end: format(b.endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
    backgroundColor: getEventBackgroundColor(b.paymentStatus),
    textColor: "black",
    extendedProps: {
      name: b.clientName,
      phone: b.clientPhoneNumber,
      price: b.priceWithDiscounts,
      range: b.durationSeconds / 3600,
      id: b.bookingId,
      comment: b.clientComment
    }
  })) || [], [data?.bookings, hallId, getEventBackgroundColor]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [info, setInfo] = useState<any>(null);
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  if (isFetching || isBookingsFetching) {
    return <Grid2 container justifyContent="center">
      <CircularProgress color="secondary" />
    </Grid2>;
  }

  return (
    <Box>
      <FullCalendar
        initialDate={convertStingToDate(searchParams.get("d")) ?? new Date()}
        datesSet={(date) =>
          setSearchParams((prev) => {
            if (!isWithinInterval(date.startStr, { start: startDate, end: endDate })) {
              prev.set("d", date.startStr ? format(date.startStr, "yyyy-MM-dd") : "");
            }

            return prev;
          })
        }
        events={tableEvents as any}
        ref={calendarRef}
        schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
        plugins={[dayGridPlugin, resourceTimeGridPlugin, interactionPlugin]}
        initialView="resourceTimeGridWeek"
        selectable
        editable
        firstDay={1}
        eventConstraint={{
          start: new Date(),
        }}
        slotMinTime="07:00:00"
        slotMaxTime="23:30:00"
        eventOverlap={false}
        eventDurationEditable={false}
        displayEventEnd
        displayEventTime
        allDaySlot
        resources={resources}
        eventDrop={eventDropped}
        eventClick={(info) => {
          setInfo(info.event.extendedProps);
          setAnchorEl(info.el as any);
        }}
      />

      <StyledPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        disableRestoreFocus
      >
        {info && (
          <Grid2 container flexDirection="column" gap={1}>
            <Typography variant="body1" weight="semiBold">{`Ім'я: ${info.name}`}</Typography>
            <Typography variant="body1" weight="semiBold">{`Телефон: ${info.phone}`}</Typography>
            <Typography variant="body1" weight="semiBold">{`Ціна: ${info.price}грн`}</Typography>
            <Typography variant="body1" weight="semiBold">{`Оренда: ${info.range}год`}</Typography>
            {
             info.comment && <Typography
                variant="body1"
                weight="semiBold"
              >{`Коментар: ${info.comment}`}</Typography>
            }

            <Link
              to={generatePath(Routes.BookingById, {
                id: info.id,
              })}
            >
              Редагувати <Edit />
            </Link>

            <Link
              to={generatePath(Routes.BookingDetails, {
                id: info.id,
              })}
            >
              Переглянути <ArrowRight />
            </Link>
          </Grid2>
        )}
      </StyledPopover>
    </Box>
  );
};

export default WeekCalendar;
